import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import FormField from "../../../components/ui/FormField";
import NumberInput from "../../../components/ui/NumberInput";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import { getSocieties } from "../../../services/societyService";
import { generateParkingSlots } from "../../../services/parkingService";
import { parkingGenerateSchema, zodErrorToFieldErrors } from "../../../schemas/parkingSlotSchema";
import styles from "./ParkingForm.module.css";

const GenerateParkingSlots = () => {
  const navigate = useNavigate();
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    society_id: "",
    four_wheeler_count: 0,
    two_wheeler_count: 0,
  });

  const loadSocieties = useCallback(async () => {
    setLoadingSocieties(true);
    try {
      const res = await getSocieties();
      setSocieties(Array.isArray(res) ? res : []);
    } finally {
      setLoadingSocieties(false);
    }
  }, []);

  useEffect(() => {
    loadSocieties();
  }, [loadSocieties]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = parkingGenerateSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(zodErrorToFieldErrors(parsed.error));
      return;
    }

    setLoading(true);
    try {
      await generateParkingSlots(parsed.data);
      navigate("/parking-management");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Parking Management">
      <div className={styles.page}>
        <form className={styles.card} onSubmit={handleSubmit}>
          <h2 className={styles.title}>Generate Parking Slots</h2>
          <div className={styles.hint}>
            This will create <b>unassigned</b> slots only. Codes will be auto-generated (Car: <b>C-001</b>, Bike:{" "}
            <b>B-001</b>).
          </div>

          <div className={styles.grid}>
            <FormField label="Society / Property" required error={errors.society_id}>
              <SelectDropdown
                value={form.society_id}
                onChange={(e) => {
                  setForm((p) => ({ ...p, society_id: e.target.value }));
                  setErrors((p) => ({ ...p, society_id: "" }));
                }}
                disabled={loadingSocieties}
                placeholder="Select society..."
                options={societies.map((s) => ({
                  label: s.society_name,
                  value: String(s.society_id),
                }))}
              />
            </FormField>

            <FormField label="4-Wheeler Parking Count" hint="Generates slots prefixed with C- (e.g. C-001)" error={errors.four_wheeler_count}>
              <NumberInput
                min={0}
                value={form.four_wheeler_count}
                onChange={(e) => {
                  setForm((p) => ({ ...p, four_wheeler_count: e.target.value }));
                  setErrors((p) => ({ ...p, four_wheeler_count: "" }));
                }}
                placeholder="e.g. 50"
                error={Boolean(errors.four_wheeler_count)}
              />
            </FormField>

            <FormField label="2-Wheeler Parking Count" hint="Generates slots prefixed with B- (e.g. B-001)" error={errors.two_wheeler_count}>
              <NumberInput
                min={0}
                value={form.two_wheeler_count}
                onChange={(e) => {
                  setForm((p) => ({ ...p, two_wheeler_count: e.target.value }));
                  setErrors((p) => ({ ...p, two_wheeler_count: "" }));
                }}
                placeholder="e.g. 100"
                error={Boolean(errors.two_wheeler_count)}
              />
            </FormField>
          </div>

          <div className={styles.actions}>
            <Button variant="outline" type="button" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Generate Slots
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default GenerateParkingSlots;

